import React, { useState, useMemo } from 'react';
import { useRisk, useHealth, useStatus, usePositions, usePnl } from '@/hooks/useDashboardQueries';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { StatusBadge } from '@/components/common/StatusBadge';
import { RiskIndicator } from '@/components/common/RiskIndicator';
import { PnlValue } from '@/components/common/PnlValue';
import { PositionRangeBar } from '@/components/common/PositionRangeBar';
import { ErrorState } from '@/components/common/ErrorState';
import { MetricCardSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { ResetCircuitBreakerModal } from './ResetCircuitBreakerModal';
import { formatINR, formatTimestamp, formatDuration } from '@/lib/utils';
import {
  ShieldAlert,
  ShieldCheck,
  Activity,
  Database,
  Cpu,
  Clock,
  Radio,
  Layers,
  Server,
  AlertOctagon,
  RefreshCw,
  RotateCcw,
  Wallet,
} from 'lucide-react';

export const RiskHealthView: React.FC = () => {
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const {
    data: riskData,
    isLoading: isRiskLoading,
    isError: isRiskError,
    error: riskError,
    refetch: refetchRisk,
  } = useRisk();

  const {
    data: healthData,
    isLoading: isHealthLoading,
    isError: isHealthError,
    error: healthError,
    refetch: refetchHealth,
  } = useHealth();

  const { data: statusData } = useStatus();
  const { data: positionsData } = usePositions();
  const { data: pnlData } = usePnl();

  const risk = riskData?.risk;
  const health = healthData?.health;
  const openPositions = useMemo(() => positionsData?.positions || [], [positionsData]);

  // Compute Active Exposure
  const activeExposure = useMemo(() => {
    return openPositions.reduce((acc, pos) => {
      const price = pos.entry_price || 0;
      return acc + pos.quantity * price;
    }, 0);
  }, [openPositions]);

  const maxPositionsLimit = positionsData?.max_positions || 5;
  const availableCapacity = Math.max(0, maxPositionsLimit - openPositions.length);
  const capacityUtilization = maxPositionsLimit > 0 ? (openPositions.length / maxPositionsLimit) * 100 : 0;

  if ((isRiskLoading || isHealthLoading) && (!risk || !health)) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Risk & System Health"
          icon={ShieldAlert}
        />
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="p-6 rounded-lg border border-slate-800 bg-slate-900/40 space-y-4">
              <MetricCardSkeleton />
              <MetricCardSkeleton />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if ((isRiskError || isHealthError) && (!risk || !health)) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Risk & System Health"
          icon={ShieldAlert}
        />
        <ErrorState
          title="Risk telemetry unavailable"
          message={
            (riskError instanceof Error ? riskError.message : '') ||
            (healthError instanceof Error ? healthError.message : 'API connection error')
          }
          onRetry={() => {
            refetchRisk();
            refetchHealth();
          }}
        />
      </div>
    );
  }

  const isKillSwitchActive = risk?.kill_switch_active ?? false;
  const isCircuitBreakerHalted = Boolean(
    pnlData?.circuit_breaker_halted ??
    pnlData?.summary?.circuit_breaker_halted ??
    risk?.circuit_breaker_halted ??
    false
  );
  const healthStatus = health?.status || 'HEALTHY';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-stagger-1">
        <PageHeader
          title="Risk & System Health Diagnostics"
          description="Autonomous risk enforcement engine, circuit breaker status, background cycle health, and database reconciliation telemetry."
          icon={ShieldAlert}
          badge={
            <div className="flex items-center gap-2">
              <StatusBadge status={healthStatus} label={`STATUS: ${healthStatus}`} size="sm" />
              <StatusBadge status="PAPER_MODE" label="PAPER MODE" size="sm" />
            </div>
          }
          actions={
            <button
              onClick={() => {
                refetchRisk();
                refetchHealth();
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 border border-slate-800 text-slate-300 text-xs font-mono hover:bg-slate-800 transition-all duration-150 active:scale-95 cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Poll Telemetry</span>
            </button>
          }
        />
      </div>

      {/* Prominent Circuit Breaker Halt Notice and Action */}
      {isCircuitBreakerHalted && (
        <div className="p-4 rounded-lg border border-rose-800/80 bg-rose-950/40 text-xs font-mono animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <ShieldAlert className="w-5 h-5 text-rose-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="font-bold text-rose-300 uppercase tracking-wide text-sm block">
                  CIRCUIT BREAKER: HALTED
                </span>
                <p className="text-xs text-rose-200/90 leading-relaxed">
                  Trading is halted by the circuit breaker. New trade allocations are currently suspended until reset.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsResetModalOpen(true)}
              className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white text-xs font-semibold shadow-md shadow-rose-950/50 transition-all active:scale-95 shrink-0 cursor-pointer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset Circuit Breaker</span>
            </button>
          </div>
        </div>
      )}

      {/* Primary Risk Overview Command Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-2">
        {/* Trading Status */}
        <MetricCard
          title="Circuit Breaker Status"
          value={isCircuitBreakerHalted ? 'HALTED' : isKillSwitchActive ? 'TRIGGERED' : 'NORMAL'}
          subtitle={
            isCircuitBreakerHalted
              ? "Today's daily circuit breaker is latched"
              : isKillSwitchActive
              ? 'Kill switch active: all entries blocked'
              : 'Operating within safe risk bounds'
          }
          variant={isCircuitBreakerHalted || isKillSwitchActive ? 'crimson' : 'emerald'}
          icon={isCircuitBreakerHalted ? AlertOctagon : ShieldCheck}
        />

        {/* Active Exposure */}
        <MetricCard
          title="Active Exposure"
          value={formatINR(activeExposure)}
          subtitle={`Across ${openPositions.length} open position${openPositions.length === 1 ? '' : 's'}`}
          variant={openPositions.length > 0 ? 'cyan' : 'default'}
          icon={Wallet}
        />

        {/* Position Capacity */}
        <MetricCard
          title="Position Capacity"
          value={`${openPositions.length} / ${maxPositionsLimit}`}
          subtitle={`${availableCapacity} available risk slots (${capacityUtilization.toFixed(0)}% utilized)`}
          variant={capacityUtilization >= 80 ? 'amber' : 'default'}
          icon={Layers}
        />

        {/* Engine Uptime */}
        <MetricCard
          title="Engine Telemetry"
          value={statusData?.uptime || formatDuration(health?.uptime_seconds) || '—'}
          subtitle={`Persistence: ${health?.database_status || 'SQLite WAL'}`}
          variant="cyan"
          icon={Cpu}
        />
      </div>

      {/* Grid: Risk Enforcement Boundaries on Left, System Health on Right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-stagger-3">
        {/* Left Column: Authoritative Risk Boundaries & Budgets */}
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/40 space-y-4 interactive-card">
          <div className="flex items-center justify-between pb-2 border-b border-slate-800">
            <h3 className="text-sm font-mono font-bold text-slate-200 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Risk Budget Thresholds</span>
            </h3>
            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950/60 border border-emerald-800/50 px-2 py-0.5 rounded">
              Oracle Guard Active
            </span>
          </div>

          <div className="space-y-3">
            <RiskIndicator
              label="Daily Loss Budget"
              current={risk?.daily_loss || 0}
              limit={risk?.daily_loss_limit || 5000}
              formatValue={(v) => formatINR(v)}
            />

            <RiskIndicator
              label="Daily Trades Limit"
              current={risk?.trades_today || 0}
              limit={risk?.max_trades_per_day || 6}
            />

            <RiskIndicator
              label="Consecutive Loss Guard"
              current={risk?.consecutive_losses || 0}
              limit={risk?.max_consecutive_losses || 3}
            />

            <RiskIndicator
              label="Position Capacity Limit"
              current={openPositions.length}
              limit={maxPositionsLimit}
            />
          </div>

          <div className="p-3 rounded-lg border border-slate-800 bg-slate-950/60 text-xs font-mono text-slate-400 space-y-1">
            <span className="text-cyan-400 font-semibold block text-[11px]">AUTONOMOUS RISK PRINCIPLE</span>
            <p className="text-[10px] text-slate-500 leading-relaxed">
              If the daily loss cap or consecutive loss threshold is breached, the trading engine autonomously pauses new order allocations until the next session reset.
            </p>
          </div>
        </div>

        {/* Right Column: Telemetry & Cycle Health */}
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/40 space-y-4 interactive-card">
          <div className="flex items-center justify-between pb-2 border-b border-slate-800">
            <h3 className="text-sm font-mono font-bold text-slate-200 flex items-center gap-2">
              <Activity className="w-4 h-4 text-cyan-400" />
              <span>System & Cycle Telemetry</span>
            </h3>
            <span className="text-[10px] font-mono text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
              Sync Active
            </span>
          </div>

          <div className="space-y-3 text-xs font-mono">
            <div className="flex items-center justify-between p-3 rounded bg-slate-950/60 border border-slate-800 transition-colors hover:border-slate-700">
              <span className="text-slate-400 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                Last Market Cycle Run
              </span>
              <span className="text-slate-200 font-semibold tabular-nums">
                {health?.last_cycle ? formatTimestamp(health.last_cycle, 'full') : 'Waiting Cycle'}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded bg-slate-950/60 border border-slate-800 transition-colors hover:border-slate-700">
              <span className="text-slate-400 flex items-center gap-2">
                <Radio className="w-3.5 h-3.5 text-cyan-400" />
                Last Telegram Signal Processed
              </span>
              <span className="text-slate-200 font-semibold tabular-nums">
                {health?.last_signal ? formatTimestamp(health.last_signal, 'full') : 'None'}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded bg-slate-950/60 border border-slate-800 transition-colors hover:border-slate-700">
              <span className="text-slate-400 flex items-center gap-2">
                <Server className="w-3.5 h-3.5 text-emerald-400" />
                Last Successful Order Check
              </span>
              <span className="text-slate-200 font-semibold tabular-nums">
                {health?.last_successful_order_check ? (
                  formatTimestamp(health.last_successful_order_check, 'full')
                ) : (
                  <span className="text-slate-500 font-normal text-[11px]">Not executed (Paper mode)</span>
                )}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded bg-slate-950/60 border border-slate-800 transition-colors hover:border-slate-700">
              <span className="text-slate-400 flex items-center gap-2">
                <Layers className="w-3.5 h-3.5 text-purple-400" />
                Last Position Reconciliation
              </span>
              <span className="text-slate-200 font-semibold tabular-nums">
                {health?.last_reconciliation ? (
                  formatTimestamp(health.last_reconciliation, 'full')
                ) : (
                  <span className="text-slate-500 font-normal text-[11px]">Not executed (Paper mode)</span>
                )}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 rounded bg-slate-950/60 border border-slate-800 transition-colors hover:border-slate-700">
              <span className="text-slate-400 flex items-center gap-2">
                <Database className="w-3.5 h-3.5 text-emerald-400" />
                Persistence Engine
              </span>
              <span className="text-emerald-400 font-semibold">
                {health?.database_status || 'SQLite WAL'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Open Position Risk Sub-Section */}
      <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/40 space-y-4 animate-stagger-4">
        <div className="flex items-center justify-between pb-2 border-b border-slate-800">
          <div>
            <h3 className="text-sm font-mono font-bold text-slate-200 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-400" />
              <span>Active Position Risk & Proximity</span>
            </h3>
            <p className="text-[11px] text-slate-400 font-sans mt-0.5">
              Live stop-loss buffers and profit target gaps across running swing positions
            </p>
          </div>
          <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">
            {openPositions.length} Active Positions
          </span>
        </div>

        {openPositions.length === 0 ? (
          <EmptyState
            title="No active position risk."
            description="When swing positions are open, real-time stop-loss buffers and target gaps will appear here."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {openPositions.map((pos) => (
              <div
                key={pos.id}
                className="p-3.5 rounded-lg bg-slate-950/60 border border-slate-800 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-slate-100 font-mono text-sm">{pos.symbol}</span>
                    <span className="px-1.5 py-0.2 rounded bg-slate-800 text-[10px] font-mono text-slate-400">
                      NSE
                    </span>
                    <span className="px-1.5 py-0.2 rounded text-[10px] font-mono font-bold bg-emerald-950/70 text-emerald-400 border border-emerald-800/60">
                      LONG
                    </span>
                  </div>
                  <div className="text-right">
                    <PnlValue
                      value={pos.unrealized_pnl}
                      percent={pos.unrealized_pnl_percent}
                      showSign={true}
                      showPercent={true}
                      layout="stacked"
                      align="right"
                      nullPlaceholder="Pending Cycle"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs font-mono pt-2 border-t border-slate-800/60">
                  <div>
                    <span className="text-[10px] text-slate-500 block">ENTRY</span>
                    <span className="text-slate-200 tabular-nums">{formatINR(pos.entry_price)}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-slate-500 block">CURRENT LTP</span>
                    {pos.ltp !== null && pos.ltp !== undefined ? (
                      <span className="text-slate-100 font-semibold tabular-nums">{formatINR(pos.ltp)}</span>
                    ) : (
                      <span className="text-[10px] text-amber-400/90 bg-amber-950/40 px-1.5 py-0.5 rounded border border-amber-900/50">
                        Pending Cycle
                      </span>
                    )}
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-800/60 bg-slate-950/40 p-2 rounded">
                  <PositionRangeBar
                    entryPrice={pos.entry_price}
                    ltp={pos.ltp}
                    stopLoss={pos.stop_loss}
                    takeProfit={pos.take_profit}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Circuit Breaker Reset Modal */}
      <ResetCircuitBreakerModal
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        dailyStats={{
          trades_today: risk?.trades_today ?? 0,
          realized_pnl: pnlData?.summary?.realized_pnl ?? 0,
          consecutive_losses: risk?.consecutive_losses ?? 0,
        }}
        limits={{
          max_trades_per_day: risk?.max_trades_per_day ?? 30,
          max_daily_loss: risk?.daily_loss_limit ?? 3000,
          max_consecutive_losses: risk?.max_consecutive_losses ?? 10,
        }}
      />
    </div>
  );
};
